/**
 * Acesso e segurança da conta: segundo fator (TOTP), sessões abertas, política de acesso da
 * organização e perfis de acesso.
 *
 * O servidor decide tudo — quem pode, o que vale, o que é recusado. Aqui ficam o contrato
 * (validado com zod: resposta fora do formato vira falha visível, nunca um estado inventado) e
 * os hooks que as telas usam. O texto de recusa vem pronto da API, escrito para a pessoa.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  apagar,
  apagarJson,
  atualizarJson,
  editarJson,
  enviarJson,
  enviarJsonSemConteudo,
  pedirJson,
} from "./api";

/* ------------------------------------------------------------------ segundo fator */

const esquemaInscricao = z.object({ segredo: z.string().min(1), uri: z.string().min(1) });
const esquemaCodigos = z.object({ codigos_de_recuperacao: z.array(z.string().min(1)) });
const esquemaEstadoDoMfa = z.object({
  ativo: z.boolean(),
  obrigatorio: z.boolean(),
  codigos_restantes: z.number().int().nonnegative(),
});
const esquemaEncerradas = z.object({ encerradas: z.number().int().nonnegative() });

export type Inscricao = z.infer<typeof esquemaInscricao>;
export type EstadoDoMfa = z.infer<typeof esquemaEstadoDoMfa>;

export const CHAVE_DO_MFA = ["conta", "mfa"] as const;
export const CHAVE_DAS_MINHAS_SESSOES = ["conta", "sessoes"] as const;

/* O passo do login: o token do desafio é a autorização, e nenhum cookie existe ainda. */

export function verificarSegundoFator(desafio: string, codigo: string): Promise<void> {
  return enviarJsonSemConteudo("/sessao/mfa", { desafio, codigo });
}

export function iniciarInscricaoNoLogin(desafio: string): Promise<Inscricao> {
  return enviarJson("/sessao/mfa/inscricao", esquemaInscricao, { desafio });
}

export async function confirmarInscricaoNoLogin(
  desafio: string,
  codigo: string,
): Promise<string[]> {
  const resposta = await enviarJson("/sessao/mfa/inscricao/confirmar", esquemaCodigos, {
    desafio,
    codigo,
  });
  return resposta.codigos_de_recuperacao;
}

/** O segredo em blocos de quatro, para digitar à mão quando a câmera não está à disposição. */
export function segredoEmBlocos(segredo: string): string {
  return segredo.match(/.{1,4}/g)?.join(" ") ?? segredo;
}

/* A própria conta. */

export function useEstadoDoMfa() {
  return useQuery({
    queryKey: CHAVE_DO_MFA,
    queryFn: ({ signal }) => pedirJson("/conta/mfa", esquemaEstadoDoMfa, { signal }),
  });
}

export function iniciarInscricaoDaConta(): Promise<Inscricao> {
  return enviarJson("/conta/mfa/inscricao", esquemaInscricao, undefined);
}

export async function confirmarInscricaoDaConta(codigo: string): Promise<string[]> {
  const resposta = await enviarJson("/conta/mfa/confirmar", esquemaCodigos, { codigo });
  return resposta.codigos_de_recuperacao;
}

export function useDesativarMfa() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: { senha: string; codigo: string }) =>
      enviarJsonSemConteudo("/conta/mfa/desativar", corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DO_MFA }),
  });
}

export function useRegerarCodigos() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: async (codigo: string) =>
      (await enviarJson("/conta/mfa/codigos", esquemaCodigos, { codigo })).codigos_de_recuperacao,
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DO_MFA }),
  });
}

export function useAposInscricao() {
  const cliente = useQueryClient();
  return () => void cliente.invalidateQueries({ queryKey: CHAVE_DO_MFA });
}

/* ---------------------------------------------------------------------- sessões */

const esquemaSessaoDaConta = z.object({
  id: z.string().min(1),
  dispositivo: z.string(),
  ip: z.string().nullable(),
  criada_em: z.string(),
  ultima_atividade_em: z.string(),
  expira_em: z.string(),
  atual: z.boolean(),
});

export type SessaoDaConta = z.infer<typeof esquemaSessaoDaConta>;

/** Quem é o dono das sessões: a própria pessoa, ou outra (quem administra o acesso). */
export type DonoDasSessoes = { tipo: "minhas" } | { tipo: "pessoa"; usuarioId: string };

function caminhoDasSessoes(dono: DonoDasSessoes): string {
  return dono.tipo === "minhas"
    ? "/conta/sessoes"
    : `/usuarios/${encodeURIComponent(dono.usuarioId)}/sessoes`;
}

export function chaveDasSessoes(dono: DonoDasSessoes) {
  return dono.tipo === "minhas"
    ? CHAVE_DAS_MINHAS_SESSOES
    : (["usuarios", dono.usuarioId, "sessoes"] as const);
}

export function useSessoesAbertas(dono: DonoDasSessoes) {
  return useQuery({
    queryKey: chaveDasSessoes(dono),
    queryFn: ({ signal }) =>
      pedirJson(caminhoDasSessoes(dono), z.array(esquemaSessaoDaConta), { signal }),
  });
}

export function useEncerrarSessao(dono: DonoDasSessoes) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (sessaoId: string) =>
      apagar(`${caminhoDasSessoes(dono)}/${encodeURIComponent(sessaoId)}`),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaveDasSessoes(dono) }),
  });
}

/** Das minhas: "encerrar as outras" (a atual fica). Das de outra pessoa: "encerrar todas". */
export function useEncerrarVarias(dono: DonoDasSessoes) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () =>
      dono.tipo === "minhas"
        ? enviarJson("/conta/sessoes/encerrar-outras", esquemaEncerradas, undefined)
        : apagarJson(caminhoDasSessoes(dono), esquemaEncerradas),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaveDasSessoes(dono) }),
  });
}

export function useRedefinirMfaDaPessoa(usuarioId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () =>
      enviarJson(
        `/usuarios/${encodeURIComponent(usuarioId)}/mfa/redefinir`,
        esquemaEncerradas,
        undefined,
      ),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ["usuarios"] });
      void cliente.invalidateQueries({ queryKey: chaveDasSessoes({ tipo: "pessoa", usuarioId }) });
    },
  });
}

/* ------------------------------------------------------- política da organização */

const esquemaPolitica = z.object({
  mfa_obrigatorio: z.boolean(),
  ips_permitidos: z.array(z.string()),
  atualizada_em: z.string().nullable().optional(),
  seu_ip: z.string().nullable().optional(),
});

export type PoliticaDeSeguranca = z.infer<typeof esquemaPolitica>;

export function chaveDaPolitica(organizacaoId: string) {
  return ["organizacoes", organizacaoId, "seguranca"] as const;
}

export function usePoliticaDaOrganizacao(organizacaoId: string) {
  return useQuery({
    queryKey: chaveDaPolitica(organizacaoId),
    queryFn: ({ signal }) =>
      pedirJson(
        `/organizacoes/${encodeURIComponent(organizacaoId)}/seguranca`,
        esquemaPolitica,
        { signal },
      ),
  });
}

export function useSalvarPolitica(organizacaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: { mfa_obrigatorio: boolean; ips_permitidos: string[] }) =>
      atualizarJson(
        `/organizacoes/${encodeURIComponent(organizacaoId)}/seguranca`,
        esquemaPolitica,
        corpo,
      ),
    onSuccess: (politica) => cliente.setQueryData(chaveDaPolitica(organizacaoId), politica),
  });
}

/** O que a pessoa digita (um por linha, ou separado por vírgula) vira a lista que a API valida. */
export function listaDeRedes(texto: string): string[] {
  return texto
    .split(/[\n,;]+/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

/* ------------------------------------------------------------------------ perfis */

const esquemaRotuloDePerfil = z.object({
  chave: z.string().min(1),
  nome: z.string().min(1),
  lado: z.enum(["interno", "cliente"]),
  personalizado: z.boolean(),
  descricao: z.string().nullable().optional(),
});

const esquemaPerfil = esquemaRotuloDePerfil.extend({
  permissoes: z.array(z.string()),
  usuarios: z.number().int().nonnegative(),
  usuarios_ativos: z.number().int().nonnegative().default(0),
  usuarios_nomes: z.array(z.string()).default([]),
});

const esquemaPermissaoDoPerfil = z.object({
  chave: z.string().min(1),
  descricao: z.string(),
  cliente_pode: z.boolean(),
  sensivel: z.boolean().default(false),
});

const esquemaPerfilDuplicado = esquemaPerfil.extend({
  origem: z.string().min(1),
  retiradas: z.array(z.string()).default([]),
});

export type RotuloDePerfil = z.infer<typeof esquemaRotuloDePerfil>;
export type PerfilDeAcesso = z.infer<typeof esquemaPerfil>;
export type PermissaoDoPerfil = z.infer<typeof esquemaPermissaoDoPerfil>;
export type PerfilDuplicado = z.infer<typeof esquemaPerfilDuplicado>;

/** As permissões que o catálogo da API marca como sensíveis: a fonte é o servidor, não uma lista daqui. */
export function permissoesSensiveis(catalogo: readonly PermissaoDoPerfil[] | undefined): ReadonlySet<string> {
  return new Set((catalogo ?? []).filter((item) => item.sensivel).map((item) => item.chave));
}

export const CHAVE_DOS_PERFIS = ["perfis"] as const;
export const CHAVE_DOS_PAPEIS = ["papeis"] as const;

/** Perfis que a tela de usuários oferece na escolha — do lado de quem pede. */
export function usePapeisAtribuiveis() {
  return useQuery({
    queryKey: CHAVE_DOS_PAPEIS,
    queryFn: ({ signal }) => pedirJson("/papeis", z.array(esquemaRotuloDePerfil), { signal }),
  });
}

export function usePerfis() {
  return useQuery({
    queryKey: CHAVE_DOS_PERFIS,
    queryFn: ({ signal }) => pedirJson("/perfis", z.array(esquemaPerfil), { signal }),
  });
}

export function useCatalogoDePermissoes() {
  return useQuery({
    queryKey: [...CHAVE_DOS_PERFIS, "permissoes"],
    queryFn: ({ signal }) =>
      pedirJson("/perfis/permissoes", z.array(esquemaPermissaoDoPerfil), { signal }),
  });
}

export function useCatalogoDePermissoesDosUsuarios() {
  return useQuery({
    queryKey: ["usuarios", "catalogo-permissoes"],
    queryFn: ({ signal }) =>
      pedirJson("/permissoes/catalogo-usuarios", z.array(esquemaPermissaoDoPerfil), { signal }),
  });
}

function aoMudarPerfis(cliente: ReturnType<typeof useQueryClient>) {
  void cliente.invalidateQueries({ queryKey: CHAVE_DOS_PERFIS });
  void cliente.invalidateQueries({ queryKey: CHAVE_DOS_PAPEIS });
}

export interface NovoPerfil {
  nome: string;
  descricao: string | null;
  lado: "interno" | "cliente";
  permissoes: string[];
}

export function useCriarPerfil() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: NovoPerfil) => enviarJson("/perfis", esquemaPerfil, corpo),
    onSuccess: () => aoMudarPerfis(cliente),
  });
}

/** A cópia é feita no servidor, que deixa de fora as permissões sensíveis e diz quais saíram. */
export function useDuplicarPerfil() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ origem, nome }: { origem: string; nome: string }) =>
      enviarJson(`/perfis/${encodeURIComponent(origem)}/duplicar`, esquemaPerfilDuplicado, { nome }),
    onSuccess: () => aoMudarPerfis(cliente),
  });
}

export function useAlterarPerfilDeAcesso() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({
      chave,
      ...corpo
    }: {
      chave: string;
      nome?: string;
      descricao?: string | null;
      permissoes?: string[];
    }) => editarJson(`/perfis/${encodeURIComponent(chave)}`, esquemaPerfil, corpo),
    onSuccess: () => aoMudarPerfis(cliente),
  });
}

export function useApagarPerfil() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (chave: string) => apagar(`/perfis/${encodeURIComponent(chave)}`),
    onSuccess: () => aoMudarPerfis(cliente),
  });
}

